/**
 * CourseAssignmentForm — create/edit a CourseAssignment.
 * Fields: type (Select), title, description, maxScore, dueDaysAfterLesson.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const ASSIGNMENT_TYPES = [
  { value: 'homework', label: 'Bài tập về nhà' },
  { value: 'quiz', label: 'Kiểm tra nhanh' },
  { value: 'project', label: 'Dự án' },
] as const;

const courseAssignmentFormSchema = z.object({
  type: z.enum(['homework', 'quiz', 'project']),
  title: z.string().min(1, 'Bắt buộc').max(300),
  description: z.string().optional().or(z.literal('')),
  maxScore: z.coerce.number().positive('Phải > 0').max(9999).optional().or(z.literal('')),
  dueDaysAfterLesson: z.coerce.number().int().positive('Phải ≥ 1').optional().or(z.literal('')),
});

export type CourseAssignmentFormValues = z.infer<typeof courseAssignmentFormSchema>;

interface CourseAssignmentFormProps {
  defaultValues?: Partial<CourseAssignmentFormValues>;
  isSubmitting?: boolean;
  onSubmit: (values: CourseAssignmentFormValues) => void;
  onCancel: () => void;
}

export function CourseAssignmentForm({
  defaultValues,
  isSubmitting,
  onSubmit,
  onCancel,
}: CourseAssignmentFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CourseAssignmentFormValues>({
    resolver: zodResolver(courseAssignmentFormSchema),
    defaultValues: {
      type: 'homework',
      title: '',
      description: '',
      maxScore: 100,
      dueDaysAfterLesson: 7,
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({
      type: 'homework',
      title: '',
      description: '',
      maxScore: 100,
      dueDaysAfterLesson: 7,
      ...defaultValues,
    });
  }, [defaultValues, reset]);

  const typeVal = watch('type');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Loại <span className="text-destructive">*</span>
        </Label>
        <Select
          value={typeVal}
          onValueChange={(v) => setValue('type', v as CourseAssignmentFormValues['type'])}
        >
          <SelectTrigger className="h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASSIGNMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.type && <p className="text-xs text-destructive">{errors.type.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="assign-title" className="text-sm font-medium">
          Tiêu đề <span className="text-destructive">*</span>
        </Label>
        <Input id="assign-title" className="h-10" placeholder="Bài tập Unit 1..." {...register('title')} />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="assign-maxScore" className="text-sm font-medium">Điểm tối đa</Label>
          <Input id="assign-maxScore" type="number" min={1} className="h-10" placeholder="100" {...register('maxScore')} />
          {errors.maxScore && <p className="text-xs text-destructive">{errors.maxScore.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="assign-due" className="text-sm font-medium">Hạn nộp (ngày sau buổi học)</Label>
          <Input id="assign-due" type="number" min={1} className="h-10" placeholder="7" {...register('dueDaysAfterLesson')} />
          {errors.dueDaysAfterLesson && (
            <p className="text-xs text-destructive">{errors.dueDaysAfterLesson.message}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="assign-description" className="text-sm font-medium">Mô tả</Label>
        <Textarea id="assign-description" rows={3} placeholder="Hướng dẫn làm bài..." {...register('description')} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lưu'}
        </Button>
      </div>
    </form>
  );
}
